import { isIdentityEnabled } from '@axilon-apps/domains-ontology-ui-state'
import { ConnectionCard } from '../../pages/SettingsPage/components/ConnectionCard'
import { SignOutCard } from '../../pages/SettingsPage/components/SignOutCard'
import { useOperatorTheme, type OperatorTheme } from '../hooks/useOperatorTheme'
import {
  OPERATOR_VIEW_OPTIONS,
  type OperatorView,
  setOperatorView,
  useOperatorView
} from '../../lib/operator-view-flag'

/**
 * Technician settings — audience, theme, and the shared proxy connection card.
 */
export function SettingsPage() {
  const operatorView = useOperatorView()
  const { theme, setTheme } = useOperatorTheme()

  return (
    <div className="tv-scroll">
      <div className="tv-page tv-settings">
        <header className="tv-page-header">
          <div>
            <h1 className="tv-page-title">Settings</h1>
            <p className="tv-page-sub">Technician preferences for this device.</p>
          </div>
        </header>

        <div className="tv-card">
          <h2 className="tv-card-title">View</h2>
          <div className="tv-setting-row">
            <span className="tv-setting-label">Audience</span>
            <AudienceRolePicker value={operatorView} onChange={setOperatorView} />
          </div>
          <div className="tv-setting-row tv-setting-row-border">
            <span className="tv-setting-label">Theme</span>
            <ThemePicker value={theme} onChange={setTheme} />
          </div>
        </div>

        <ConnectionCard />
        {isIdentityEnabled() && <SignOutCard />}
      </div>
    </div>
  )
}

function AudienceRolePicker({
  value,
  onChange
}: {
  value: OperatorView
  onChange: (next: OperatorView) => void
}) {
  return (
    <div className="tv-seg" role="radiogroup" aria-label="Role view">
      {OPERATOR_VIEW_OPTIONS.map((option) => {
        const active = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={active ? 'active' : undefined}
            onClick={() => onChange(option.value)}
            title={option.description}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function ThemePicker({
  value,
  onChange
}: {
  value: OperatorTheme
  onChange: (next: OperatorTheme) => void
}) {
  return (
    <div className="tv-seg" role="radiogroup" aria-label="Color theme">
      {(
        [
          { theme: 'light', label: 'Light' },
          { theme: 'dark', label: 'Dark' }
        ] as const
      ).map(({ theme, label }) => (
        <button
          key={theme}
          type="button"
          role="radio"
          aria-checked={value === theme}
          className={value === theme ? 'active' : undefined}
          onClick={() => onChange(theme)}
        >
          {label}
        </button>
      ))}
    </div>
  )
}
